/**
 * components/Chip
 * ──────────────────────────────────────────────────────────────────────────
 * A CHOOSABLE chip. Two shapes, both from the design:
 *
 *   pill     `4px 10px`, 999px radius, 11px — the register's scope filters and
 *            the custodian's assignment switcher.
 *   control  29px tall, 5px radius, 12px — every choice field inside a modal.
 *
 * WHY MODAL CHOICE FIELDS ARE CHIPS AND NOT DROPDOWNS
 * A native `<select>` takes two clicks to move between fields and re-renders
 * awkwardly on every platform in a different way. A chip row is one click, and
 * on a phone it needs no picker sheet at all. Holder type, scope, instrument,
 * funding source, currency, category, rule trigger and approvers are all chip
 * rows for that reason — see ChipRow, which lays them out.
 *
 * Selected is ALWAYS ink fill / white text. There is no second selected style.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useState } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { controlHeight } from '../../theme/spacing';
import { sans, size as fontSize } from '../../theme/typography';
import { isWeb } from '../../lib/responsive';

export type ChipShape = 'pill' | 'control';

export interface ChipProps {
  label: string;
  selected?: boolean;
  onPress?: () => void;
  shape?: ChipShape;
  disabled?: boolean;
  /** Filter chips are capitalised; option values keep their own casing. */
  capitalize?: boolean;
  testID?: string;
}

export function Chip({
  label,
  selected = false,
  onPress,
  shape = 'control',
  disabled = false,
  capitalize = false,
  testID,
}: ChipProps) {
  const [hovered, setHovered] = useState(false);
  const isPill = shape === 'pill';
  // An unselected chip darkens its border on hover; a selected one has nowhere
  // darker to go, so it stays put.
  const showHover = hovered && isWeb && !disabled && !selected;

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ selected, disabled }}
      testID={testID}
      style={[
        styles.base,
        isPill ? styles.pill : styles.control,
        {
          borderColor: selected ? neutral.ink : showHover ? neutral.borderStrong : neutral.border,
          backgroundColor: selected ? neutral.ink : neutral.surface,
          opacity: disabled ? 0.45 : 1,
        },
      ]}
    >
      <Text
        numberOfLines={1}
        style={[
          isPill ? styles.pillLabel : styles.controlLabel,
          { color: selected ? neutral.surface : neutral.ink2 },
          capitalize && styles.capitalize,
        ]}
      >
        {label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    flexShrink: 0,
  },
  pill: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: radii.pill,
  },
  control: {
    height: controlHeight.sm,
    paddingHorizontal: 11,
    borderRadius: radii.control,
  },
  pillLabel: { ...sans('400'), fontSize: fontSize.metaSm },
  controlLabel: { ...sans('500'), fontSize: fontSize.label },
  capitalize: { textTransform: 'capitalize' },
});

export default Chip;
